import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [paymentData, setPaymentData] = useState<{
    serviceType: string
    serviceTitle: string
    amount: number
    calendlyUrl?: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pollingAttempts, setPollingAttempts] = useState(0)
  const [clientEmail, setClientEmail] = useState<string | null>(null)

  const sessionId = searchParams.get('session_id')
  const MAX_POLLING_ATTEMPTS = 10 // Poll for up to 10 attempts (about 10 seconds)
  const POLLING_INTERVAL = 1000 // 1 second between polls

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found')
      setLoading(false)
      return
    }

    // Start polling for payment status
    pollPaymentStatus(sessionId)
  }, [sessionId])

  const pollPaymentStatus = async (sessionId: string, attempt: number = 0) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${API_URL}/payment-status?session_id=${sessionId}`)
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.processed && data.payment) {
          // Payment has been processed by webhook
          const calendlyBaseUrl = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/peytonjkclark'
          const calendlyUrl = data.payment.metadata?.calendlyUrl || calendlyBaseUrl

          setPaymentData({
            serviceType: data.payment.service_type || 'service',
            serviceTitle: data.payment.service_title || `${data.payment.service_type || 'Service'} Service`,
            amount: data.payment.amount ? data.payment.amount / 100 : 0,
            calendlyUrl,
          })
          
          // Store client email if available
          if (data.client?.email) {
            setClientEmail(data.client.email)
          }
          
          setLoading(false)
          return
        } else if (attempt < MAX_POLLING_ATTEMPTS) {
          // Payment not processed yet, continue polling
          setPollingAttempts(attempt + 1)
          setTimeout(() => {
            pollPaymentStatus(sessionId, attempt + 1)
          }, POLLING_INTERVAL)
          return
        } else {
          // Max attempts reached, show success page anyway (webhook may be delayed)
          console.log('Max polling attempts reached, showing success page')
          const calendlyBaseUrl = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/peytonjkclark'
          setPaymentData({
            serviceType: 'service',
            serviceTitle: 'Service',
            amount: 0,
            calendlyUrl: calendlyBaseUrl,
          })
          setLoading(false)
        }
      } else {
        // API error, fallback to direct database query
        fetchPaymentDataDirectly(sessionId)
      }
    } catch (err) {
      console.error('Error polling payment status:', err)
      // Fallback to direct database query
      fetchPaymentDataDirectly(sessionId)
    }
  }

  const fetchPaymentDataDirectly = async (sessionId: string) => {
    try {
      // Try to fetch payment record from database
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .select('*, clients(email, name)')
        .eq('stripe_session_id', sessionId)
        .single()

      if (payment && !paymentError) {
        // Payment found in database
        const calendlyBaseUrl = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/peytonjkclark'
        const calendlyUrl = payment.metadata?.calendlyUrl || calendlyBaseUrl

        setPaymentData({
          serviceType: payment.service_type,
          serviceTitle: payment.service_title || `${payment.service_type} Service`,
          amount: payment.amount / 100, // Convert from cents to dollars
          calendlyUrl,
        })
        
        // Extract client email if available
        if (payment.clients) {
          const client = Array.isArray(payment.clients) ? payment.clients[0] : payment.clients
          if (client?.email) {
            setClientEmail(client.email)
          }
        }
        
        setLoading(false)
        return
      }

      // Fallback: Show generic success message with default Calendly URL
      const calendlyBaseUrl = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/peytonjkclark'
      setPaymentData({
        serviceType: 'service',
        serviceTitle: 'Service',
        amount: 0,
        calendlyUrl: calendlyBaseUrl,
      })
      setLoading(false)
    } catch (err) {
      console.error('Error fetching payment data:', err)
      // Even on error, show success page with default Calendly URL
      const calendlyBaseUrl = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/peytonjkclark'
      setPaymentData({
        serviceType: 'service',
        serviceTitle: 'Service',
        amount: 0,
        calendlyUrl: calendlyBaseUrl,
      })
      setLoading(false)
    }
  }


  if (loading) {
    return (
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-text-primary/70 mb-2">Processing your payment...</p>
          {pollingAttempts > 0 && (
            <p className="text-sm text-text-primary/50">
              Waiting for confirmation ({pollingAttempts}/{MAX_POLLING_ATTEMPTS})
            </p>
          )}
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="neu p-8 rounded-2xl max-w-md w-full text-center"
        >
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold mb-4 text-text-primary">Error</h2>
          <p className="text-text-primary/70 mb-6">{error}</p>
          <motion.button
            onClick={() => navigate('/')}
            className="px-6 py-3 neu rounded-xl font-semibold text-text-primary neu-hover"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            Back to Home
          </motion.button>
        </motion.div>
      </section>
    )
  }

  return (
    <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background">
      <div className="container mx-auto max-w-2xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
          className="neu p-8 sm:p-12 rounded-2xl text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
            className="text-6xl sm:text-7xl mb-6"
          >
            ✅
          </motion.div>

          {/* Success Message */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 font-display leading-tight">
            <span className="text-gradient-glow">Payment</span>{' '}
            <span className="text-text-primary">Successful!</span>
          </h1>

          <p className="text-lg sm:text-xl text-text-primary/70 mb-8">
            Thank you for your purchase. Your payment has been processed successfully.
          </p>

          {/* Payment Details */}
          {paymentData && (
            <div className="neu p-6 rounded-xl mb-8 text-left max-w-md mx-auto">
              <h3 className="text-sm font-semibold text-text-primary/70 uppercase tracking-wide mb-4">
                Payment Details
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-text-primary/70">Service:</span>
                  <span className="text-text-primary font-medium">{paymentData.serviceTitle}</span>
                </div>
                {paymentData.amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-text-primary/70">Amount:</span>
                    <span className="text-text-primary font-semibold">
                      ${paymentData.amount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-text-primary/70">Status:</span>
                  <span className="text-accent font-medium">✓ Completed</span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="space-y-4 mb-8">
            <h3 className="text-xl font-semibold text-text-primary mb-4">
              What's Next?
            </h3>
            <div className="space-y-3 text-left max-w-md mx-auto">
              <div className="flex items-start gap-3">
                <span className="text-accent text-xl mt-0.5">1.</span>
                <div>
                  <p className="text-text-primary font-medium mb-1">Access Your Client Portal</p>
                  <p className="text-sm text-text-primary/70">
                    Go to your client portal to view your session credits and book appointments.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-accent text-xl mt-0.5">2.</span>
                <div>
                  <p className="text-text-primary font-medium mb-1">Check Your Email</p>
                  <p className="text-sm text-text-primary/70">
                    You'll receive a receipt and booking confirmation shortly.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-accent text-xl mt-0.5">3.</span>
                <div>
                  <p className="text-text-primary font-medium mb-1">Access Your Portal</p>
                  <p className="text-sm text-text-primary/70">
                    View your bookings and payment history in your client portal.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={async () => {
                // Navigate to client portal with payment info
                if (clientEmail) {
                  // Use stored client email from polling
                  try {
                    const API_URL = import.meta.env.VITE_API_URL || '/api'
                    const tokenResponse = await fetch(`${API_URL}/generate-portal-token`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: clientEmail }),
                    })
                    
                    if (tokenResponse.ok) {
                      const tokenData = await tokenResponse.json()
                      if (tokenData.token) {
                        navigate(`/portal?token=${tokenData.token}&email=${encodeURIComponent(clientEmail)}`, { replace: true })
                        return
                      }
                    }
                  } catch (tokenError) {
                    console.error('Error generating portal token:', tokenError)
                  }
                  
                  // Fallback: redirect to portal with email only
                  navigate(`/portal?email=${encodeURIComponent(clientEmail)}`, { replace: true })
                  return
                }
                
                // If no client email stored, try to fetch from payment
                if (paymentData && sessionId) {
                  try {
                    const { data: paymentRecord, error: paymentError } = await supabase
                      .from('payments')
                      .select('id, client_id, clients(email)')
                      .eq('stripe_session_id', sessionId)
                      .single()
                    
                    if (paymentRecord && !paymentError) {
                      let email = ''
                      if (Array.isArray(paymentRecord.clients) && paymentRecord.clients.length > 0) {
                        email = (paymentRecord.clients[0] as any)?.email || ''
                      } else if (paymentRecord.clients && typeof paymentRecord.clients === 'object') {
                        email = (paymentRecord.clients as any)?.email || ''
                      }
                      
                      if (email) {
                        navigate(`/portal?email=${encodeURIComponent(email)}`, { replace: true })
                        return
                      }
                    }
                  } catch (error) {
                    console.error('Error fetching payment:', error)
                  }
                }
                
                // Final fallback: redirect to portal
                navigate('/portal', { replace: true })
              }}
              className="px-8 py-4 neu rounded-xl font-semibold text-text-primary neu-hover text-base sm:text-lg min-h-[52px] bg-accent/10 hover:bg-accent/20 transition-colors shadow-lg"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              Go to Client Portal →
            </motion.button>
            <motion.button
              onClick={() => navigate('/')}
              className="px-8 py-4 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary transition-colors neu-hover text-base sm:text-lg min-h-[52px]"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              Back to Home
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

